import * as React from 'react';
import * as Yup from 'yup';

import { Button, FormGroup, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';

import { DialogStatus } from 'common/components/dialogs/DialogStatus';
import { FormMoneyInput } from 'common/components/form/FormMoneyInput';
import { Formik, FormikProps } from 'formik';
import { formatMoneyValue } from 'common/utils/format';
import { toFormGroupStyle } from 'common/utils/form';
import { FormHelp } from 'common/components/form/FormHelp';
import { formatDecimal } from '../../common/utils/format';
import { Product } from 'common/api/models/Product';
import { Order } from 'common/api/models/Order';
import { AppState } from 'common/models/AppState';
import { FormInput } from 'common/components/form/FormInput';

export interface Refund {
  amount: number;
  checkNumber?: string;
  adjustBalance: boolean;
  productId?: string;
}
interface RefundDialogProps {
  isOpen: boolean;
  onSave: (saved: Refund) => void;
  onCancel: () => void;
  error?: string;
  order?: Order;
  appState: AppState;
  isLoading?: boolean;
  refundAmount: number;
  orderAmount: number;
  includeCheckNumber: boolean;
}
interface RefundDialogStates {
  hardIncludeCheckNumber: boolean;
}
class RefundValues {
  amount = '';
  checkNumber = '';
  productId = '';
  adjustBalance: boolean;
}

export class RefundDialog extends React.Component<RefundDialogProps, RefundDialogStates> {
  validationSchema = Yup.object().shape({
    amount: Yup.number().required('Required'),
    checkNumber: Yup.number().required('Required'),
  });

  state = {
    hardIncludeCheckNumber: false,
  };

  initialValues(products: Product[]) {
    return {
      amount: formatMoneyValue(this.props.refundAmount),
      checkNumber: '',
      adjustBalance: false,
      productId: products && products[0] ? products[0].id : '',
    };
  }

  handleSubmit = (values: RefundValues) => {
    let submit: Refund = {
      amount: Number(formatDecimal(values.amount)),
      adjustBalance: values.adjustBalance,
      productId: values.productId ? values.productId : undefined,
    };
    if (this.props.includeCheckNumber || this.state.hardIncludeCheckNumber) {
      submit.checkNumber = values.checkNumber;
    }
    this.props.onSave(submit);
  };

  render() {
    const { order } = this.props;
    const products: Product[] = order
      ? order.items!.map(item => {
          return this.props.appState.entities.product[item.productId];
        })
      : [];
    const { hardIncludeCheckNumber } = this.state;
    return (
      <Modal size="md" isOpen={this.props.isOpen} toggle={this.props.onCancel} keyboard={true}>
        <Formik
          initialValues={this.initialValues(products)}
          onSubmit={this.handleSubmit}
          render={(form: FormikProps<RefundValues>) => (
            <form onSubmit={form.handleSubmit} data-testid="refundDialogForm">
              <ModalHeader toggle={this.props.onCancel}>Issue Refund</ModalHeader>
              <ModalBody>
                <FormMoneyInput id="amount" name="amount" label="Refund Amount" />
                {!this.props.includeCheckNumber ? (
                  <label>
                    <input
                      type="checkbox"
                      checked={hardIncludeCheckNumber}
                      onChange={() => this.setState({ hardIncludeCheckNumber: !hardIncludeCheckNumber })}
                    />{' '}
                    Use Check
                  </label>
                ) : null}
                {(this.props.includeCheckNumber || hardIncludeCheckNumber) && (
                  <FormInput label="Outgoing Check Number" name="checkNumber" id="checkNumber" />
                )}
                <FormGroup color={toFormGroupStyle('adjustBalance', form)}>
                  <label>
                    <input
                      type="checkbox"
                      id="adjustBalance"
                      checked={form.values.adjustBalance}
                      onChange={form.handleChange}
                    />{' '}
                    {parseInt(formatDecimal(form.values.amount), 10) >= this.props.orderAmount && (
                      <span>
                        Cancel Order{' '}
                        <FormHelp text="If this box is checked, the refund will automatically cancel the order. If not, the order will stay in pending status." />
                      </span>
                    )}
                    {parseInt(formatDecimal(form.values.amount), 10) < this.props.orderAmount && (
                      <span>
                        Reduce Balance Due{' '}
                        <FormHelp text="If this box is checked, the refund will reduce the balance due by the amount of the refund. If this box is not checked, the member will still owe the original amount of the order." />
                      </span>
                    )}
                  </label>
                </FormGroup>
                {form.values.adjustBalance &&
                  parseInt(formatDecimal(form.values.amount), 10) < this.props.orderAmount && (
                    <FormInput
                      label={
                        <React.Fragment>
                          Reduce Balance for Product
                          <FormHelp text="This is the product on the order that will be credited with the adjustment." />
                        </React.Fragment>
                      }
                      tag="select"
                      id="productId"
                      name="productId"
                    >
                      {products.map(p => (
                        <option key={p.id} value={p.id}>
                          {p.name} {p.sku}
                        </option>
                      ))}
                    </FormInput>
                  )}
              </ModalBody>
              <ModalFooter>
                <div>
                  <Button color="secondary" onClick={this.props.onCancel}>
                    Cancel
                  </Button>{' '}
                  <Button color="primary" type="submit" disabled={this.props.isLoading}>
                    Refund
                  </Button>
                </div>
              </ModalFooter>
              <DialogStatus error={this.props.error} isLoading={this.props.isLoading} />
            </form>
          )}
        />
      </Modal>
    );
  }
}



// WEBPACK FOOTER //
// ./src/screens/Orders/RefundDialog.tsx